import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { abandonedLeadApiService } from "../../../service/admin/abandonedLeadApiService";
import moment from "moment";
import { getAbandonedLeadDisplayAmounts } from "./abandonedLeadPricing";
import "./leadDetail.css";

const fmt = (n) =>
  n != null && !isNaN(Number(n))
    ? `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
    : "—";

const VALID_STAGES = new Set(["cart", "payment"]);

export default function AbandonedLeadDetail() {
  const { stage: stageParam, id } = useParams();
  const stage = String(stageParam || "").toLowerCase();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [proceeding, setProceeding] = useState(false);
  const [proceedMsg, setProceedMsg] = useState(null);
  const [showProceedModal, setShowProceedModal] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});
  const toggleProduct = (key) => setExpandedProducts((prev) => ({ ...prev, [key]: !prev[key] }));
  const [proceedNote, setProceedNote] = useState("");
  const [showAddrModal, setShowAddrModal] = useState(false);
  const [savingAddr, setSavingAddr] = useState(false);
  const [addrMsg, setAddrMsg] = useState(null);
  const emptyAddr = () => ({
    firstName: "",
    lastName: "",
    phone: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    pincode: "",
  });
  const [shipAddr, setShipAddr] = useState(emptyAddr());
  const [billAddr, setBillAddr] = useState(emptyAddr());
  const [sameAsBilling, setSameAsBilling] = useState(false);

  useEffect(() => {
    if (!VALID_STAGES.has(stage) || !id) {
      setLoading(false);
      setError("Invalid lead URL.");
      return;
    }
    fetchLead();
  }, [id, stage]);

  const fetchLead = () => {
    if (!VALID_STAGES.has(stage) || !id) return;
    setLoading(true);
    abandonedLeadApiService
      .getAbandonedLeadById(stage, id)
      .then((res) => {
        if (res?.status?.code === 200 && res?.data) setData(res.data);
        else setError("Lead not found.");
      })
      .catch(() => setError("Failed to load lead details."))
      .finally(() => setLoading(false));
  };

  const handleProceedOrder = async () => {
    setShowProceedModal(false);
    setProceeding(true);
    setProceedMsg(null);
    try {
      const res = await abandonedLeadApiService.adminProceedAbandonedOrder(stage, id, {
        customerNote: proceedNote.trim(),
      });
      if (res?.status?.code === 200) {
        setProceedMsg({ type: "success", text: "Order created successfully! Reloading…" });
        setTimeout(() => fetchLead(), 1400);
      } else {
        setProceedMsg({
          type: "error",
          text: res?.error?.message || res?.status?.message || "Failed to create order.",
        });
      }
    } catch (e) {
      setProceedMsg({ type: "error", text: e?.message || "Server error. Try again." });
    } finally {
      setProceeding(false);
    }
  };

  const openAddrModal = () => {
    const lead = data?.lead || {};
    const sa = lead.shippingAddress || emptyAddr();
    const ba = lead.billingAddress || emptyAddr();
    const same = JSON.stringify(sa) === JSON.stringify(ba);
    setShipAddr({ ...emptyAddr(), ...sa });
    setBillAddr({ ...emptyAddr(), ...ba });
    setSameAsBilling(same);
    setAddrMsg(null);
    setShowAddrModal(true);
  };

  const handleSaveAddress = async () => {
    setSavingAddr(true);
    setAddrMsg(null);
    try {
      const billing = sameAsBilling ? { ...shipAddr } : billAddr;
      const res = await abandonedLeadApiService.updateAbandonedLeadAddress(stage, id, shipAddr, billing);
      if (res?.status?.code === 200) {
        setAddrMsg({ type: "success", text: "Address saved!" });
        setTimeout(() => {
          setShowAddrModal(false);
          fetchLead();
        }, 900);
      } else {
        setAddrMsg({ type: "error", text: res?.error?.message || "Failed to save." });
      }
    } catch (e) {
      setAddrMsg({ type: "error", text: e?.message || "Server error." });
    } finally {
      setSavingAddr(false);
    }
  };

  if (loading)
    return (
      <div className="ld-page">
        <div className="ld-loading">
          <div className="ld-spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  if (error || !data)
    return (
      <div className="ld-page">
        <div className="ld-error">{error || "No data."}</div>
      </div>
    );

  const { lead, order, cartItems } = data;
  const isOpen = lead.status === "open";
  const stageLabel = stage === "payment" ? "Payment" : "Cart";
  const amt = getAbandonedLeadDisplayAmounts(lead);

  const grandTotal = order
    ? (Number(order.mkp_current_total_price) || 0) + (Number(order.totalShippingCharges) || 0)
    : amt.grandEstimated;

  const statusTimeline = order?.orderStatus || [];
  const orderDetails = order?.orderDetails || [];

  const leadStatusLabel =
    lead.status === "open" ? "Open" : lead.status === "converted" ? "Order Placed" : lead.status === "cancelled" ? "Cancelled" : lead.status;

  return (
    <div className="ld-page">
      <div className="ld-topbar">
        <button className="ld-back-btn" onClick={() => navigate(`/admin/abandoned-leads/${stage}`)}>
          ← Back to {stageLabel} leads
        </button>
        <div className="ld-topbar-actions">
          {isOpen && (
            <button
              className="ld-proceed-btn"
              onClick={() => {
                setProceedNote("");
                setShowProceedModal(true);
              }}
              disabled={proceeding}
            >
              {proceeding ? "Creating Order…" : "✔ Proceed Order"}
            </button>
          )}
          {!isOpen && order && (
            <button className="ld-view-order-btn" onClick={() => navigate(`/admin/orders/${order._id}`)}>
              View in Orders →
            </button>
          )}
        </div>
      </div>

      {proceedMsg && <div className={`ld-banner ld-banner-${proceedMsg.type}`}>{proceedMsg.text}</div>}

      <h2 className="ld-page-title">{order ? "ORDER INFORMATION" : "LEAD INFORMATION"}</h2>

      <div className="ld-panels">
        <div className="ld-panel">
          <div className="ld-panel-heading">{order ? "ORDER DETAILS" : "LEAD DETAILS"}</div>
          {order ? (
            <>
              <LabelVal label="IMPORTERR ORDER ID" value={order.upsaleoOrderId || "—"} bold />
              <LabelVal label="MARKETPLACE ORDER ID" value={order.orderId || "—"} />
              <LabelVal label="MARKETPLACE" value={order.marketplace || "IMPORTERR.COM"} />
              {order.bankTransferTxnId && <LabelVal label="TXN / UTR ID" value={order.bankTransferTxnId} />}
            </>
          ) : (
            <>
              <LabelVal label="STAGE" value={stageLabel} />
              <LabelVal label="LEAD ID" value={`#${lead._id?.toString().slice(-6).toUpperCase()}`} bold />
              <LabelVal label="RECORDED ON" value={moment(lead.createdAt).format("Do MMM YYYY, h:mm a")} />
              <LabelVal
                label={lead.stage === "cart" ? "Product total (excl. shipping)" : "Est. total (incl. shipping)"}
                value={fmt(lead.stage === "cart" ? amt.productSubtotal : amt.grandEstimated)}
                bold
              />
            </>
          )}
        </div>

        <div className="ld-panel">
          <div className="ld-panel-heading">STATUS & TRACKING</div>
          {order ? (
            <>
              <LabelVal
                label="ORDER DATE"
                value={
                  order.orderDate
                    ? moment(order.orderDate).format("Do MMM YYYY, h:mm a")
                    : moment(lead.createdAt).format("Do MMM YYYY, h:mm a")
                }
              />
              <LabelVal label="ORDER STATUS">
                <span className={`ld-status-chip ld-status-${(order.status || "").replace(/\s+/g, "-")}`}>{order.status}</span>
              </LabelVal>
              <LabelVal label="SHIPPING METHOD" value={order.shippingMethod || "—"} />
              <LabelVal label="TRACKING NUMBER" value={orderDetails.find((d) => d.trackingId)?.trackingId || "—"} />
            </>
          ) : (
            <>
              <LabelVal label="LEAD STATUS">
                <span
                  className={`ld-status-chip ${
                    lead.status === "open"
                      ? "ld-status-interested"
                      : lead.status === "converted"
                      ? "ld-status-paid"
                      : lead.status === "cancelled"
                      ? "ld-status-cancel"
                      : "ld-status-interested"
                  }`}
                >
                  {leadStatusLabel}
                </span>
              </LabelVal>
              <LabelVal label="DATE" value={moment(lead.createdAt).format("Do MMM YYYY, h:mm a")} />
              <LabelVal label="ORDER STATUS" value="No order placed yet" muted />
            </>
          )}
        </div>

        <div className="ld-panel">
          <div
            className="ld-panel-heading"
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <span>{order ? "RECIPIENT INFORMATION" : "CUSTOMER INFORMATION"}</span>
            {!order && (
              <button className="ld-edit-addr-btn" onClick={openAddrModal}>
                {data?.lead?.shippingAddress ? "✎ Edit Address" : "+ Add Address"}
              </button>
            )}
          </div>
          {order ? (
            <>
              <LabelVal label="RECIPIENT NAME" value={order.recipientName || lead.name || "—"} />
              <LabelVal label="TELEPHONE" value={order.shippingPhoneNumber || lead.mobile || "—"} />
              <LabelVal
                label="SHIPPING ADDRESS"
                value={[order.shippingAddress1, order.shippingAddress2, order.shippingCity, order.shippingState, order.shippingPostalCode]
                  .filter(Boolean)
                  .join(", ")}
              />
              <LabelVal
                label="BILLING ADDRESS"
                value={[order.billingAddress1, order.billingAddress2, order.billingCity, order.billingState, order.billingPostalCode]
                  .filter(Boolean)
                  .join(", ")}
              />
            </>
          ) : (
            <>
              <LabelVal label="NAME" value={lead.name || "—"} />
              <LabelVal label="EMAIL" value={lead.email || "—"} link={lead.email ? `mailto:${lead.email}` : null} />
              <LabelVal label="MOBILE" value={lead.mobile || "—"} link={lead.mobile ? `tel:${lead.mobile}` : null} />
              {lead.shippingAddress && (
                <LabelVal
                  label="SHIPPING ADDRESS"
                  value={[
                    lead.shippingAddress.address1,
                    lead.shippingAddress.address2,
                    lead.shippingAddress.city,
                    lead.shippingAddress.state,
                    lead.shippingAddress.pincode,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                />
              )}
              {lead.billingAddress && (
                <LabelVal
                  label="BILLING ADDRESS"
                  value={[
                    lead.billingAddress.address1,
                    lead.billingAddress.address2,
                    lead.billingAddress.city,
                    lead.billingAddress.state,
                    lead.billingAddress.pincode,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                />
              )}
            </>
          )}
        </div>

        <div className="ld-panel ld-panel-price">
          <div className="ld-panel-heading">PRICE SUMMARY</div>
          {order ? (
            <>
              <div className="ld-price-row">
                <span>PRODUCT TOTAL</span>
                <span className="ld-price-val">{fmt(order.mkp_current_total_price)}</span>
              </div>
              <div className="ld-price-row">
                <span>SHIPPING CHARGES</span>
                <span className="ld-price-val">{fmt(order.totalShippingCharges)}</span>
              </div>
              <div className="ld-price-total-row">
                <span>GRAND TOTAL</span>
                <span className="ld-price-grand">{fmt(grandTotal)}</span>
              </div>
            </>
          ) : (
            <>
              <div className="ld-price-row">
                <span>PRODUCT TOTAL</span>
                <span className="ld-price-val">{fmt(amt.productSubtotal)}</span>
              </div>
              <div className="ld-price-row">
                <span>SHIPPING CHARGES</span>
                <span className="ld-price-val">{fmt(amt.lmd)}</span>
              </div>
              <div className="ld-price-total-row">
                <span>ESTIMATED TOTAL</span>
                <span className="ld-price-grand">{fmt(amt.grandEstimated)}</span>
              </div>
            </>
          )}
        </div>
      </div>

      {statusTimeline.length > 0 && (
        <div className="ld-timeline-card">
          <div className="ld-timeline-track">
            {statusTimeline.map((s, i) => (
              <div key={i} className="ld-timeline-step">
                <div className="ld-timeline-dot" />
                {i < statusTimeline.length - 1 && <div className="ld-timeline-line" />}
                <div className="ld-timeline-label">{s.status}</div>
                {s.date && <div className="ld-timeline-date">{moment(s.date).format("Do MMM YYYY, h:mm a")}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {(() => {
        const sourceItems = orderDetails.length > 0 ? orderDetails : Array.isArray(cartItems) ? cartItems : [];
        if (sourceItems.length === 0) return null;

        const groups = new Map();
        sourceItems.forEach((item) => {
          const pid = String(item.productId || item._id || item.sku || Math.random());
          if (!groups.has(pid)) groups.set(pid, []);
          groups.get(pid).push(item);
        });

        return (
          <div className="ld-table-card">
            <div className="ld-prod-header">
              <span className="ld-prod-h-img"></span>
              <span className="ld-prod-h-name">NAME</span>
              <span className="ld-prod-h-sku">SKU</span>
              <span className="ld-prod-h-qty">QTY</span>
              <span className="ld-prod-h-toggle"></span>
            </div>

            {Array.from(groups.entries()).map(([pid, items]) => {
              const first = items[0];
              const isOrder = orderDetails.length > 0;
              const productName = first.productName || "—";
              const productSku = isOrder
                ? first.variant?.product?.sku || first.productSku || "—"
                : first.productSku || "—";
              const productImage = isOrder ? first.productImage : first.image;
              const totalQty = items.reduce((s, i) => s + (Number(i.quantity) || 0), 0);
              const pdpUrl =
                productSku && productSku !== "—"
                  ? `${window.location.origin}/product-details/${productSku}/${encodeURIComponent(
                      productName.toLowerCase().replace(/\s+/g, "-")
                    )}`
                  : null;
              const isExpanded = !!expandedProducts[pid];

              return (
                <div key={pid} className="ld-prod-group">
                  <div className="ld-prod-row" onClick={() => toggleProduct(pid)}>
                    <span className="ld-prod-img-cell">
                      {productImage ? (
                        <img
                          src={productImage}
                          alt=""
                          className="ld-tbl-img"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            pdpUrl && window.open(pdpUrl, "_blank");
                          }}
                          style={{ cursor: pdpUrl ? "pointer" : "default" }}
                        />
                      ) : (
                        <div className="ld-tbl-img-placeholder" />
                      )}
                    </span>
                    <span className="ld-prod-name-cell">
                      <span
                        className={pdpUrl ? "ld-tbl-link" : "ld-tbl-name"}
                        onClick={(e) => {
                          e.stopPropagation();
                          pdpUrl && window.open(pdpUrl, "_blank");
                        }}
                      >
                        {productName}
                      </span>
                    </span>
                    <span className="ld-prod-sku-cell">
                      <span className="ld-tbl-sku">{productSku}</span>
                    </span>
                    <span className="ld-prod-qty-cell">{totalQty}</span>
                    <span className="ld-prod-toggle-cell">
                      <span className={`ld-chevron ${isExpanded ? "ld-chevron-open" : ""}`}>›</span>
                    </span>
                  </div>

                  {isExpanded && (
                    <div className="ld-variant-rows">
                      <div className="ld-variant-header">
                        <span>VARIANT SKU</span>
                        <span>ATTRIBUTES</span>
                        <span>QTY</span>
                        <span>PRICE</span>
                        <span>TYPE</span>
                      </div>
                      {items.map((item, idx) => {
                        const variantSku = isOrder ? item.sku || item.variant?.skuId || "—" : item.sku || "—";
                        const attrs = isOrder
                          ? Array.isArray(item.variant?.skuAttributes)
                            ? item.variant.skuAttributes.map(
                                (a) => `${a.attributeName || ""}: ${a.value || a.attributeValue || ""}`
                              )
                            : []
                          : typeof item.variant === "string"
                          ? item.variant.split(", ").filter(Boolean)
                          : [];
                        const price = isOrder ? item.consumerPrice : item.unitPrice;
                        const pricingMode = item.pricingMode || (isOrder ? "dropshipping" : "dropshipping");
                        return (
                          <div key={idx} className="ld-variant-row">
                            <span className="ld-var-sku">{variantSku}</span>
                            <span className="ld-var-attrs">
                              {attrs.length > 0 ? (
                                attrs.map((a, i) => (
                                  <span key={i} className="ld-attr-tag">
                                    {a}
                                  </span>
                                ))
                              ) : (
                                <span className="ld-muted">—</span>
                              )}
                            </span>
                            <span className="ld-var-qty">{item.quantity || "—"}</span>
                            <span className="ld-var-price">{fmt(price)}</span>
                            <span className="ld-var-type">
                              <span className={`ld-type-chip ld-type-${pricingMode === "wholesale" ? "wholesale" : "drop"}`}>
                                {pricingMode === "wholesale" ? "Wholesale" : "Dropshipping"}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {order?.paymentProofUrl && (
        <div className="ld-table-card" style={{ padding: "18px 20px" }}>
          <div className="ld-section-title">PAYMENT PROOF</div>
          {order.paymentProofUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
            <a href={order.paymentProofUrl} target="_blank" rel="noopener noreferrer">
              <img src={order.paymentProofUrl} alt="Payment proof" className="ld-proof-img" />
            </a>
          ) : (
            <a href={order.paymentProofUrl} target="_blank" rel="noopener noreferrer" className="ld-ext-link">
              View Proof Document ↗
            </a>
          )}
        </div>
      )}

      {showProceedModal && (
        <div className="ld-modal-overlay" onClick={() => setShowProceedModal(false)}>
          <div className="ld-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ld-modal-header">
              <h3 className="ld-modal-title">Confirm Order Creation</h3>
              <button className="ld-modal-close" onClick={() => setShowProceedModal(false)}>
                ✕
              </button>
            </div>
            <div className="ld-modal-body">
              <p className="ld-modal-desc">
                This will create an order for <strong>{data?.lead?.name || "this user"}</strong> using their current cart
                and default shipping address on file.
              </p>
              <div className="ld-modal-summary">
                <span>Products Total</span>
                <span>{fmt(amt.productSubtotal)}</span>
              </div>
              <div className="ld-modal-summary">
                <span>Shipping Charges</span>
                <span>{fmt(amt.lmd)}</span>
              </div>
              <div className="ld-modal-summary ld-modal-total">
                <span>Grand Total</span>
                <span>{fmt(amt.grandEstimated)}</span>
              </div>
              <div className="ld-modal-field" style={{ marginTop: "14px" }}>
                <label className="ld-modal-label">Order Note (visible in order management)</label>
                <textarea
                  className="ld-modal-input"
                  rows={3}
                  placeholder="Add a note for the sales team or warehouse…"
                  value={proceedNote}
                  onChange={(e) => setProceedNote(e.target.value)}
                />
              </div>
            </div>
            <div className="ld-modal-footer">
              <button className="ld-modal-cancel-btn" onClick={() => setShowProceedModal(false)}>
                Cancel
              </button>
              <button className="ld-modal-confirm-btn" onClick={handleProceedOrder}>
                ✔ Create Order
              </button>
            </div>
          </div>
        </div>
      )}

      {showAddrModal && (
        <div className="ld-modal-overlay" onClick={() => setShowAddrModal(false)}>
          <div className="ld-modal ld-addr-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ld-modal-header">
              <h3 className="ld-modal-title">{data?.lead?.shippingAddress ? "Edit Address" : "Add Address"}</h3>
              <button className="ld-modal-close" onClick={() => setShowAddrModal(false)}>
                ✕
              </button>
            </div>
            <div className="ld-modal-body">
              <p className="ld-addr-section-title">Shipping Address</p>
              <AddrForm addr={shipAddr} setAddr={setShipAddr} />
              <div className="ld-addr-same-row">
                <input type="checkbox" id="sameAddr" checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)} />
                <label htmlFor="sameAddr">Billing address same as shipping</label>
              </div>
              {!sameAsBilling && (
                <>
                  <p className="ld-addr-section-title" style={{ marginTop: "18px" }}>
                    Billing Address
                  </p>
                  <AddrForm addr={billAddr} setAddr={setBillAddr} />
                </>
              )}
              {addrMsg && <p className={`ld-addr-msg ld-addr-msg-${addrMsg.type}`}>{addrMsg.text}</p>}
            </div>
            <div className="ld-modal-footer">
              <button className="ld-modal-cancel-btn" onClick={() => setShowAddrModal(false)}>
                Cancel
              </button>
              <button className="ld-modal-confirm-btn" onClick={handleSaveAddress} disabled={savingAddr}>
                {savingAddr ? "Saving…" : "✔ Save Address"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddrForm({ addr, setAddr }) {
  const f = (field) => (e) => setAddr((prev) => ({ ...prev, [field]: e.target.value }));
  return (
    <div className="ld-addr-grid">
      <div className="ld-addr-field">
        <label>First Name</label>
        <input className="ld-modal-input" value={addr.firstName} onChange={f("firstName")} placeholder="First name" />
      </div>
      <div className="ld-addr-field">
        <label>Last Name</label>
        <input className="ld-modal-input" value={addr.lastName} onChange={f("lastName")} placeholder="Last name" />
      </div>
      <div className="ld-addr-field ld-addr-full">
        <label>Phone</label>
        <input className="ld-modal-input" value={addr.phone} onChange={f("phone")} placeholder="Phone number" />
      </div>
      <div className="ld-addr-field ld-addr-full">
        <label>Address Line 1</label>
        <input className="ld-modal-input" value={addr.address1} onChange={f("address1")} placeholder="Street address, building…" />
      </div>
      <div className="ld-addr-field ld-addr-full">
        <label>
          Address Line 2 <span style={{ color: "#9ca3af" }}>(optional)</span>
        </label>
        <input className="ld-modal-input" value={addr.address2} onChange={f("address2")} placeholder="Apartment, floor, landmark…" />
      </div>
      <div className="ld-addr-field">
        <label>City</label>
        <input className="ld-modal-input" value={addr.city} onChange={f("city")} placeholder="City" />
      </div>
      <div className="ld-addr-field">
        <label>State</label>
        <input className="ld-modal-input" value={addr.state} onChange={f("state")} placeholder="State" />
      </div>
      <div className="ld-addr-field">
        <label>Pincode</label>
        <input className="ld-modal-input" value={addr.pincode} onChange={f("pincode")} placeholder="Pincode" />
      </div>
    </div>
  );
}

function LabelVal({ label, value, children, bold, muted, link }) {
  return (
    <div className="ld-lv-row">
      <span className="ld-lv-label">{label}</span>
      {children ? (
        <span className="ld-lv-value">{children}</span>
      ) : link ? (
        <a href={link} className="ld-lv-link">
          {value}
        </a>
      ) : (
        <span className={`ld-lv-value${bold ? " ld-bold" : ""}${muted ? " ld-muted" : ""}`}>{value || "—"}</span>
      )}
    </div>
  );
}
